import { log } from '../../logs'
import {
  formatExpireTime,
  isWatermarkDrawn,
  setWatermarkDrawn,
  removeOmniPreviewTokenFromLocalStorage,
  removeOmniPreviewTokenFromURL,
  setExpireAlerted,
  isExpireAlerted,
} from '../../utils/preview-watermark-utils'
import type { IOmniPreviewTokenInfo } from '../../interfaces/omni-api'
import drawWatermark from '../../utils/draw-watermaker.js'

/**
 * 比较两个 URL 是否只有 hash 不同
 * @param url1 - 第一个 URL
 * @param url2 - 第二个 URL
 * @returns 如果只有 hash 不同返回 true，否则返回 false
 */
function isOnlyHashDifferent(url1: string, url2: string): boolean {
  try {
    // 找到 hash 的位置
    const hashIndex1 = url1.indexOf('#')
    const hashIndex2 = url2.indexOf('#')

    // 获取去掉 hash 后的 URL
    const url1WithoutHash = hashIndex1 !== -1 ? url1.substring(0, hashIndex1) : url1
    const url2WithoutHash = hashIndex2 !== -1 ? url2.substring(0, hashIndex2) : url2

    return url1WithoutHash === url2WithoutHash
  } catch (error) {
    log.error('isOnlyHashDifferent: URL 比较时发生错误', error)
    return false
  }
}

const onExitPreview = () => {
  log.info('onExitPreview: 退出预览模式')

  try {
    // 1. 移除 localStorage 中的 omni_preview_token
    removeOmniPreviewTokenFromLocalStorage()

    // 2. 移除 URL 中的 omni_preview_token 参数
    const cleanedURL = removeOmniPreviewTokenFromURL()
    const currentURL = window.location.href

    log.debug('onExitPreview: URL 比较', { currentURL, cleanedURL })

    // 3. 判断是否只有 hash 不同
    const onlyHashDifferent = isOnlyHashDifferent(currentURL, cleanedURL)

    window.location.href = cleanedURL
    log.info('onExitPreview: 已退出预览模式，页面即将刷新')

    if (onlyHashDifferent) {
      // 只有 hash 不同，使用 reload 强制刷新
      log.info('onExitPreview: 检测到只有 hash 不同，使用 reload 强制刷新')
      window.location.reload()
    }
  } catch (error) {
    log.error('onExitPreview: 退出预览时发生错误', error)
  }
}

/**
 * 绘制预览水印
 * @param previewTokenInfo - 预览token信息
 * @returns void
 * @example
 * drawPreviewWatermark({ expiresAt:  })
 */
export function drawPreviewWatermark(previewTokenInfo: IOmniPreviewTokenInfo) {
  try {
    // 检查是否已经绘制过水印
    if (isWatermarkDrawn()) {
      log.debug('drawPreviewWatermark: 水印已绘制，跳过重复绘制')
      return
    }

    // 检查 previewTokenInfo 是否有效
    if (!previewTokenInfo || !previewTokenInfo.expiresAt) {
      log.warn('drawPreviewWatermark: previewTokenInfo 或 expiresAt 无效', previewTokenInfo)
      return
    }

    // 格式化过期时间
    const formattedExpireTime = formatExpireTime(previewTokenInfo.expiresAt)
    if (formattedExpireTime === '无效时间') {
      log.error('drawPreviewWatermark: 过期时间格式化失败', previewTokenInfo.expiresAt)
      return
    }

    // 构建水印文本
    const watermarkText = `预览模式中，预览过期时间为 ${formattedExpireTime}`
    log.debug('drawPreviewWatermark: 准备绘制水印', { watermarkText })

    // 动态导入 drawWatermark 函数

    // 检查 drawWatermark 函数是否存在
    if (typeof drawWatermark !== 'function') {
      log.error('drawPreviewWatermark: drawWatermark 函数不存在或不是函数')
      return
    }

    // 检查 DOM 是否可用
    if (typeof document === 'undefined' || !document.body) {
      log.error('drawPreviewWatermark: DOM 不可用，无法绘制水印')
      return
    }

    // 调用 drawWatermark 函数绘制水印
    drawWatermark({
      watermarkName: watermarkText,
      fontSize: 20,
      exitButtonText: '退出预览',
      onClickExit: onExitPreview,
    })

    // 设置水印已绘制状态
    setWatermarkDrawn(true)
    log.info('drawPreviewWatermark: 水印绘制成功', { watermarkText })
  } catch (error) {
    log.error('drawPreviewWatermark: 绘制水印时发生错误', error)
    // 不抛出错误，避免影响主流程
  }
}

/**
 * 绘制预览失败水印
 * @returns void
 * @example
 * drawPreviewWatermark({ expiresAt:  })
 */
export function drawPreviewFailedWatermark(watermarkText: string) {
  try {
    // 检查是否已经绘制过水印
    if (isWatermarkDrawn()) {
      log.debug('drawPreviewWatermark: 水印已绘制，跳过重复绘制')
      return
    }

    // 构建水印文本
    log.debug('drawPreviewWatermark: 准备绘制水印', { watermarkText })

    // 动态导入 drawWatermark 函数

    // 检查 drawWatermark 函数是否存在
    if (typeof drawWatermark !== 'function') {
      log.error('drawPreviewWatermark: drawWatermark 函数不存在或不是函数')
      return
    }

    // 检查 DOM 是否可用
    if (typeof document === 'undefined' || !document.body) {
      log.error('drawPreviewWatermark: DOM 不可用，无法绘制水印')
      return
    }

    // 调用 drawWatermark 函数绘制水印
    drawWatermark({
      watermarkName: watermarkText,
      fontSize: 20,
      exitButtonText: '退出预览',
      onClickExit: onExitPreview,
    })

    // 设置水印已绘制状态
    setWatermarkDrawn(true)
    log.info('drawPreviewWatermark: 水印绘制成功', { watermarkText })
  } catch (error) {
    log.error('drawPreviewWatermark: 绘制水印时发生错误', error)
    // 不抛出错误，避免影响主流程
  }
}

/**
 * 检查响应中是否包含预览token信息
 * @param response - API响应对象
 * @returns 如果包含 previewTokenInfo 返回 true，否则返回 false
 * @example
 * hasPreviewTokenInfo({ previewTokenInfo: { expiresAt: '2024-01-15T00:00:00Z' } }) // true
 * hasPreviewTokenInfo({ data: {} }) // false
 */
export function hasPreviewTokenInfo(response: any): boolean {
  try {
    return !!(response && response.previewTokenInfo && response.previewTokenInfo.expiresAt)
  } catch (error) {
    log.error('hasPreviewTokenInfo: 检查 previewTokenInfo 时发生错误', error)
    return false
  }
}

const alertPreviewFailedMessage = (message: string) => {
  if (!message) {
    return
  }
  if (isExpireAlerted()) {
    log.debug('alertPreviewFailedMessage: 过期提醒已弹出，跳过重复提醒', { message })
    return
  }
  alert(message)
  setExpireAlerted(true)
}

/**
 * 处理 OMNI API 响应，如果包含预览token信息则绘制水印
 * 10008: 预览 token 已过期
 * 10002: 预览 token 非法
 * @param response - API响应对象
 * @returns void
 * @example
 * await handleOmniResponsePreview({ previewTokenInfo: { expiresAt: 1768443015 } })
 */
export function handleOmniResponsePreview(response: any): void {
  try {
    // 检查响应状态码
    if (response?.status === 10008) {
      log.info('预览时长已到期，需到OMNI后台重新配置后再使用~')
      drawPreviewFailedWatermark('预览时长已到期，需到OMNI后台重新配置后再使用~')
      alertPreviewFailedMessage('预览时长已到期，需到OMNI后台重新配置后再使用~')
      return
    }

    // 检查响应状态码
    if (response?.status === 10002) {
      log.info('预览 token 无效')
      drawPreviewFailedWatermark('当前 Token 无效，需在 OMNI 后台完成配置更新后再使用~')
      alertPreviewFailedMessage('当前 Token 无效，需在 OMNI 后台完成配置更新后再使用~')
      return
    }

    // 检查响应中是否包含预览token信息
    if (hasPreviewTokenInfo(response)) {
      log.debug('handleOmniResponsePreview: 检测到预览token信息，准备绘制水印')
      drawPreviewWatermark(response.previewTokenInfo)
    } else {
      log.debug('handleOmniResponsePreview: 响应中不包含预览token信息，跳过水印绘制')
    }
  } catch (error) {
    log.error('handleOmniResponsePreview: 处理响应时发生错误', error)
    // 不抛出错误，避免影响主流程
  }
}
